import { useCallback, useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import { useStores } from "stores/setup/use-store";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { MacroRegion } from "enum/MacroRegion";
import { useHistory, useLocation } from "react-router-dom";
import { Star } from "lucide-react";
import { useSaveCustomPoint } from "hooks/useSaveCustomPoint";
import { StationSearchInput } from "./Components/StationSearchInput";
import { IStation } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { getMacroRegionOriginFromPoint } from "util/macroRegion/getMacroRegionOriginFromPoint";
import { createCustomPoint } from "util/station/createCustomPoint";

function PointSelectionMapControls() {
  const {
    dataStores: {
      forecastChartPageStore,
      insightAlertStore,
      regionStore,
      authStore,
    },
  } = useStores();

  const map = useMap();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const alertId = queryParams.get("alert_id");
  const createAlert = queryParams.get("create_alert");
  const origin = queryParams.get("origin");
  const eventLocationId = queryParams.get("event_location_id");
  const controlRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.insightAlert",
  });

  const selectedStation = insightAlertStore.selectedStationForAlert;
  const isCustomPoint = selectedStation?.station_id === 0;
  const isPointInvalid = insightAlertStore.isPointInvalid;

  const {
    isSaveModalOpen,
    onSaveModalClose,
    stationName,
    setStationName,
    isSaving,
    handleSaveStation,
    handleOpenSaveModal,
  } = useSaveCustomPoint({ station: selectedStation, t });

  // Disable map click/scroll/double-click propagation on the control panel
  useEffect(() => {
    if (controlRef.current) {
      L.DomEvent.disableClickPropagation(controlRef.current);
      L.DomEvent.disableScrollPropagation(controlRef.current);
    }
  }, []);

  // Add drawing-mode class to leaflet controls to adjust position
  useEffect(() => {
    const leafletTopElement = document.querySelector(".leaflet-top");

    if (leafletTopElement) {
      if (forecastChartPageStore.isPointSelectionMode) {
        leafletTopElement.classList.add("drawing-mode");
      } else {
        leafletTopElement.classList.remove("drawing-mode");
      }
    }

    return () => {
      if (leafletTopElement) {
        leafletTopElement.classList.remove("drawing-mode");
      }
    };
  }, [forecastChartPageStore.isPointSelectionMode]);

  // Center map on selected station when it changes
  useEffect(() => {
    if (selectedStation && map) {
      map.setView(
        [selectedStation.station_lat, selectedStation.station_lon],
        map.getZoom(),
      );
    }
  }, [selectedStation, map]);

  const onCancel = useCallback(() => {
    // Restore original station if it exists (user clicked cancel without changes)
    if (insightAlertStore.originalStationForAlert) {
      insightAlertStore.setSelectedStationForAlert(
        insightAlertStore.originalStationForAlert,
      );
      insightAlertStore.setOriginalStationForAlert(null);
    } else {
      insightAlertStore.setSelectedStationForAlert(null);
      insightAlertStore.setAlertLocationType(null);
    }
    insightAlertStore.setIsPointInvalid(false);
    forecastChartPageStore.setIsPointSelectionMode(false);

    // If editing event location, redirect back to event manager
    if (eventLocationId && origin === "event-manager") {
      history.push(
        `/insight-manager?tab=1&edit_event_location_id=${eventLocationId}`,
      );
    }
    // If creating event from insight-manager, redirect back with drawer open param
    else if (createAlert === "true" && origin === "insight-manager") {
      history.push("/insight-manager?tab=1&open_create_drawer=true");
    }
    // If creating alert from insight-alerts, redirect back with drawer open param
    else if (createAlert === "true" && origin === "insight-alerts") {
      history.push("/insight-alerts?open_create_drawer=true");
    }
    // If editing an existing alert, redirect back to the alerts page
    else if (alertId) {
      history.push(`/insight-alerts?alert_id=${alertId}`);
    }
  }, [
    insightAlertStore,
    forecastChartPageStore,
    alertId,
    eventLocationId,
    createAlert,
    origin,
    history,
  ]);

  const onConfirm = useCallback(() => {
    if (!selectedStation) return;

    // Set the macro region origin based on the station
    if (selectedStation.macro_region_origin) {
      insightAlertStore.setMacroRegionOrigin(
        selectedStation.macro_region_origin as MacroRegion,
      );
    } else {
      // Try to find macro region from regionStore
      const macroRegion = regionStore.macroRegions?.find(
        (region) => region.id === selectedStation.region_id,
      );
      if (macroRegion?.origin) {
        insightAlertStore.setMacroRegionOrigin(
          macroRegion.origin as MacroRegion,
        );
      }
    }

    // Clear the original station since user confirmed the new selection
    insightAlertStore.setOriginalStationForAlert(null);
    insightAlertStore.setIsPointInvalid(false);

    // Disable point selection mode - drawer will reappear
    forecastChartPageStore.setIsPointSelectionMode(false);

    // If editing event location, redirect back to event manager
    if (eventLocationId && origin === "event-manager") {
      history.push(
        `/insight-manager?tab=1&edit_event_location_id=${eventLocationId}`,
      );
    }
    // If creating event from insight-manager, redirect back with drawer open param
    else if (createAlert === "true" && origin === "insight-manager") {
      history.push("/insight-manager?tab=1&open_create_drawer=true");
    }
    // If creating alert from insight-alerts, redirect back with drawer open param
    else if (createAlert === "true" && origin === "insight-alerts") {
      history.push("/insight-alerts?open_create_drawer=true");
    }
    // If editing an existing alert, redirect back to the alerts page
    // Pass station data via URL to avoid MobX state loss during navigation
    else if (alertId && selectedStation) {
      const stationParams = `&selected_lat=${
        selectedStation.station_lat
      }&selected_lon=${selectedStation.station_lon}&selected_station_id=${
        selectedStation.station_id
      }&selected_station_name=${encodeURIComponent(
        selectedStation.station_name,
      )}`;
      history.push(
        `/insight-alerts?alert_id=${alertId}&returning_from_selection=true${stationParams}`,
      );
    } else if (alertId) {
      history.push(
        `/insight-alerts?alert_id=${alertId}&returning_from_selection=true`,
      );
    }
  }, [
    selectedStation,
    insightAlertStore,
    forecastChartPageStore,
    regionStore.macroRegions,
    alertId,
    eventLocationId,
    createAlert,
    origin,
    history,
  ]);

  const onClearSelection = useCallback(() => {
    insightAlertStore.setSelectedStationForAlert(null);
  }, [insightAlertStore]);

  const handleSearchSelectStation = useCallback(
    (station: IStation) => {
      insightAlertStore.setSelectedStationForAlert(
        station,
        (station.macro_region_origin as MacroRegion) ?? null,
      );
      insightAlertStore.setIsPointInvalid(false);
      if (station.macro_region_origin) {
        insightAlertStore.setMacroRegionOrigin(
          station.macro_region_origin as MacroRegion,
        );
      }
    },
    [insightAlertStore],
  );

  const handleSearchSelectCoordinates = useCallback(
    (lat: number, lon: number) => {
      const macroRegion = getMacroRegionOriginFromPoint(
        lat,
        lon,
        regionStore.macroRegions,
        authStore.userMarcroRegions as MacroRegion[],
      );

      if (!macroRegion) {
        insightAlertStore.setIsPointInvalid(true);
        return;
      }

      insightAlertStore.setIsPointInvalid(false);
      const customPoint = createCustomPoint(lat, lon, macroRegion);

      insightAlertStore.setSelectedStationForAlert(customPoint, macroRegion);
      insightAlertStore.setMacroRegionOrigin(macroRegion);
    },
    [insightAlertStore, regionStore.macroRegions, authStore.userMarcroRegions],
  );

  return (
    <>
      <Box
        ref={controlRef}
        position="absolute"
        top="7px"
        left="20px"
        zIndex={1000}
        background="white"
        padding="16px"
        borderRadius="8px"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
        width="350px"
        maxWidth="350px"
      >
        <Flex direction="column" gap={4}>
          <Text
            fontSize="14px"
            fontWeight="medium"
            color="#0884CC"
            lineHeight="1.4"
            textAlign="center"
          >
            {t("selectPointOrStationOnMap")}
          </Text>

          <StationSearchInput
            stations={forecastChartPageStore.stations}
            onSelectStation={handleSearchSelectStation}
            onSelectCoordinates={handleSearchSelectCoordinates}
          />

          {isPointInvalid && (
            <Alert rounded="base" status="warning" color="gray.700">
              <AlertIcon />
              <AlertDescription lineHeight={"20px"} fontSize={"small"}>
                {t("invalidPoint")}
              </AlertDescription>
            </Alert>
          )}

          {selectedStation && (
            <Box bg="gray.50" py={2} px={3} borderRadius="md">
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="sm" color="gray.700">
                    <Text as="span" fontWeight="medium">
                      {isCustomPoint
                        ? t("customPoint")
                        : selectedStation.station_name}
                    </Text>
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    ({selectedStation.station_lat.toFixed(4)},{" "}
                    {selectedStation.station_lon.toFixed(4)})
                  </Text>
                </Box>
                {isCustomPoint && !authStore.readOnlyUser && (
                  <Button
                    size="xs"
                    variant="ghost"
                    color="newBlue.500"
                    leftIcon={<Star size={14} />}
                    onClick={handleOpenSaveModal}
                    whiteSpace="nowrap"
                  >
                    {t("saveStation")}
                  </Button>
                )}
              </Flex>
            </Box>
          )}

          <Box>
            <Flex gap={3} justify="space-between">
              <Button
                flex={1}
                color="newBlue.500"
                variant="outline"
                size="sm"
                onClick={onCancel}
              >
                {t("backToPrevious")}
              </Button>

              <Button
                flex={1}
                layerStyle="ocean"
                _hover={{ layerStyle: "ocean" }}
                size="sm"
                onClick={onConfirm}
                isDisabled={!selectedStation}
              >
                {t("confirmPoint")}
              </Button>
            </Flex>
            {selectedStation && (
              <Flex mt={2} justify="center">
                <Text
                  color="newBlue.500"
                  textDecoration="underline"
                  cursor="pointer"
                  fontSize="sm"
                  onClick={onClearSelection}
                >
                  {t("clearSelection")}
                </Text>
              </Flex>
            )}
          </Box>
        </Flex>
      </Box>

      {/* Modal for saving custom point as station */}
      <Modal isOpen={isSaveModalOpen} onClose={onSaveModalClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="white">
          <ModalHeader color="#1A202C">{t("saveStation")}</ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel color="#2D3748">{t("stationName")}</FormLabel>
              <Input
                value={stationName}
                onChange={(e) => setStationName(e.target.value)}
                placeholder={t("stationNamePlaceholder") as string}
                autoFocus
                color="#1A202C"
              />
            </FormControl>
            {selectedStation && (
              <Text fontSize="sm" color="#718096" mt={2}>
                {t("coordinates")}: {selectedStation.station_lat.toFixed(4)},{" "}
                {selectedStation.station_lon.toFixed(4)}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onSaveModalClose}
              color="#4A5568"
            >
              {t("cancel")}
            </Button>
            <Button
              layerStyle="ocean"
              _hover={{ layerStyle: "ocean" }}
              onClick={handleSaveStation}
              isLoading={isSaving}
              isDisabled={!stationName.trim()}
            >
              {t("save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default observer(PointSelectionMapControls);
